import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Search, Trash2 } from '@/components/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { StatusBadge, PriorityBadge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { deleteMrn, listMrns, submitMrn, type Mrn, type MrnItem } from '@/api/mrn';
import { formatDate } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { MrnFormModal } from './MrnFormModal';
import { Pagination } from '@/components/ui/Pagination';

export function MrnPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const docViewer = useDocViewer();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // MRN date window — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  useEffect(() => setPage(1), [range]);
  const [pageSize, setPageSize] = useState(25);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Mrn | null>(null);
  const [seedItems, setSeedItems] = useState<MrnItem[] | undefined>(undefined);

  // Items handed over from the catalogue cart ("Raise MRN") open the New MRN form
  // pre-filled. Consume the router state once so a refresh doesn't re-open it.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const seed = (location.state as { mrnSeed?: MrnItem[] } | null)?.mrnSeed;
    if (seed && seed.length) {
      setEditing(null);
      setSeedItems(seed);
      setModalOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const filter = useMemo(
    () => ({
      status: status || undefined,
      from: range?.from,
      to: range?.to,
      search: search || undefined,
      page,
      pageSize,
    }),
    [status, range, search, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['mrn', filter],
    queryFn: () => listMrns(filter),
    placeholderData: keepPreviousData,
  });

  const delMutation = useMutation({
    mutationFn: deleteMrn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mrn'] });
      toast.push('MRN deleted', 'info');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const submitMutation = useMutation({
    mutationFn: submitMrn,
    onSuccess: (mrn) => {
      qc.invalidateQueries({ queryKey: ['mrn'] });
      qc.invalidateQueries({ queryKey: ['mrn-pending'] });
      toast.pushStep(`MRN ${mrn.mrnNo} submitted`, {
        body: 'Waiting on HOD approval before moving to Inventory Check.',
        href: '/purchase/hod',
        nextLabel: 'Open HOD Approval',
        tag: `mrn-submitted-${mrn.id}`,
      });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Submit failed'), 'error'),
  });

  function openAdd() {
    setEditing(null);
    setSeedItems(undefined);
    setModalOpen(true);
  }
  function openEdit(m: Mrn) {
    setEditing(m);
    setModalOpen(true);
  }
  async function onDelete(m: Mrn) {
    if (!await confirmDlg(`Delete ${m.mrnNo}? This cannot be undone.`)) return;
    delMutation.mutate(m.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Material Requisition Note"      />

      <FilterBar>
        {/* Search takes the full row on mobile and grows to fill remaining
            space on wider screens; the other fields sit at their natural
            widths and share the leftovers. */}
        <FilterField label="Search" className="col-span-2 sm:w-[280px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8"
              aria-label="Search MRNs"
            />
          </div>
        </FilterField>
        <FilterField label="Status" className="sm:w-[160px]">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>Draft</option>
            <option>Pending</option>
            <option>Pending HOD</option>
            <option>Approved</option>
            <option>Rejected</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Create button lives inside the filter block on both viewports.
            Mobile: fills the empty row-2 slot in the 3-col grid.
            Desktop: right-pinned via ml-auto. */}
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ New MRN
        </button>
      </FilterBar>

      <Table<Mrn>
        storageKey="mrn"
        title="MRN Records"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          {
            key: 'mrnNo',
            header: 'MRN #',
            width: '130px',
            cell: (r) => (
              <span className="font-extrabold text-accent" title={r.source === 'Sales-Short' ? 'Raised automatically for a sales shortfall' : undefined}>
                {r.mrnNo}
              </span>
            ),
          },
          { key: 'date', header: 'Date', width: '100px', align: 'center', cell: (r) => formatDate(r.mrnDate) },
          { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
          { key: 'dept', header: 'Department', width: '120px', align: 'center', cell: (r) => r.department },
          { key: 'raisedBy', header: 'Raised By', width: '120px', align: 'center', cell: (r) => r.raisedBy },
          { key: 'priority', header: 'Priority', width: '90px', align: 'center', cell: (r) => <PriorityBadge priority={r.priority} /> },
          { key: 'purpose', header: 'Purpose', width: '130px', align: 'center', cell: (r) => r.purpose },
          { key: 'ref', header: 'Ref', width: '80px', align: 'center', cell: (r) => <span className="text-[11px] font-bold text-[#a78bfa]">{r.refDoc ?? r.enqRef ?? '—'}</span> },
          { key: 'items', header: 'Items', width: '60px', align: 'center', cell: (r) => r.items.length },
          { key: 'reqDate', header: 'Required', width: '100px', align: 'center', cell: (r) => formatDate(r.requiredBy ?? '') },
          { key: 'status', header: 'Status', width: '100px', align: 'center', cell: (r) => <StatusBadge status={r.status} /> },
          { key: 'sla', header: 'SLA', width: '90px', align: 'center', cell: (r) => <SlaBadge sla={r.sla ?? null} /> },
          {
            key: 'actions',
            header: '',
            width: '120px',
            cell: (r) => (
              <div className="flex items-center justify-center gap-1">
                <Button variant="glass" size="sm" onClick={() => openEdit(r)}>View</Button>
                <Button variant="glass" size="sm" onClick={() => docViewer.open('mrn', r.id, r.mrnNo)} title="Open A4 document"><FileText size={14} /></Button>
                {r.status === 'Draft' && (
                  <Button variant="glass" size="sm" onClick={() => submitMutation.mutate(r.id)} title="Submit for approval">Submit</Button>
                )}
                {r.status === 'Draft' && (
                  <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete"><Trash2 size={14} /></Button>
                )}
              </div>
            ),
          },
        ]}
        footer={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        }
      />

      <MrnFormModal open={modalOpen} editing={editing} seedItems={seedItems} onClose={() => { setModalOpen(false); setSeedItems(undefined); }} />
    </div>
  );
}
